/**
 * Query keys de billing — facturación y gasto: facturas, gastos, clientes, proveedores, presupuestos y servicios.
 *
 * Se componen en `src/lib/qk.ts`, que sigue siendo el único punto de import de
 * toda la app (`@/lib/qk`). Aquí solo vive la definición; añadir una clave nueva
 * de este dominio se hace en este fichero y aparece sola en `qk`.
 *
 * Las convenciones (entidad en singular primero, `companyId` después, subsegmentos
 * en medio) están explicadas en `src/lib/qk.ts` y siguen aplicando.
 */
export const billingKeys = {
  // Escaneos OCR. `all()` invalida todo lo de OCR de un golpe (pendientes incluidos).
  ocrScan: {
    all: () => ['ocr-scans'] as const,
    // Lista de GastosEscaneosView (array ordenado; nadie más la lee).
    list: (companyId?: string) => ['ocr-scans', companyId] as const,
    // Pendientes de la Bandeja (L-02b §3.3): una sola entrada para la página y el
    // contador del menú. Otra forma de datos que `list`, por eso otro segmento.
    pendientes: (companyId?: string) => ['ocr-scans', 'pending', companyId] as const,
    // Panel de estadísticas OCR (pages/OcrStats.jsx).
    stats: (companyId?: string, period?: string) => ['ocr-stats', companyId, period] as const,
  },
  expense: {
    /** Top-level prefix — invalidates every Expense query in one shot. */
    all: () => ['expense'] as const,
    /** Used by `useExpenses` (no pagination). Matches the legacy factory shape. */
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['expense', companyId, filters] as const,
    /** Used by `usePaginatedExpenses`. Mirrors the legacy factory shape. */
    paginated: (
      companyId?: string,
      page = 0,
      pageSize = 10,
      filters: Record<string, unknown> = {},
    ) => ['expense', 'paginated', companyId, page, pageSize, filters] as const,
    /** Dashboard expense chart widget. */
    chart: (companyId?: string) => ['expense', 'chart', companyId] as const,
    /** "Gastos por categoría" dashboard widget. */
    byCategory: (companyId?: string) => ['expense', 'by-category', companyId] as const,
    /** "Gastos recientes" dashboard widget. */
    recent: (companyId?: string) => ['expense', 'recent', companyId] as const,
    /** Monthly stats card on the Gastos page. */
    statsMonth: (companyId?: string, from?: string, to?: string) =>
      ['expense', 'stats-month', companyId, from, to] as const,
    /** KPIs + aging + tab counts del chrome de la lista (PR-G2). */
    statsBreakdown: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['expense', 'stats-breakdown', companyId, filters] as const,
    /** Cuenta que usaría el asiento + si conviene que el usuario la elija. */
    accountPreview: (companyId?: string, expenseId?: string) =>
      ['expense', 'account-preview', companyId, expenseId] as const,
    /** Últimos gastos de un proveedor: duplicados y «como tu último gasto». Bajo
     *  `expense` para que se refresque al guardar uno. */
    bySupplier: (companyId?: string, supplierId?: string) =>
      ['expense', 'by-supplier', companyId, supplierId] as const,
  },

  customer: {
    all: () => ['customers'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['customers', companyId, filters] as const,
    // Customer 360 — ficha unificada (/clientes/:id). Prefijo 'customers' →
    // invalidar la entidad invalida también detail/overview.
    detail: (companyId?: string, id?: string) =>
      ['customers', 'detail', companyId, id] as const,
    overview: (companyId?: string, id?: string) =>
      ['customers', 'overview', companyId, id] as const,
  },

  quote: {
    all: () => ['quotes'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['quotes', companyId, filters] as const,
  },

  supplier: {
    all: () => ['suppliers'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['suppliers', companyId, filters] as const,
    // Supplier 360 — ficha unificada (/proveedores/:id). Prefijo 'suppliers' →
    // invalidar la entidad invalida también el detalle.
    detail: (companyId?: string, id?: string) =>
      ['suppliers', 'detail', companyId, id] as const,
  },

  // VEN-4 — Catálogo de Servicios (/api/services sobre products.type='service').
  // Clave propia (NO comparte caché con product → no acopla Inventario).
  service: {
    all: () => ['services'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['services', companyId, filters] as const,
  },

  invoice: {
    all: () => ['invoice'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['invoice', companyId, filters] as const,
    paginated: (
      companyId?: string,
      page = 0,
      pageSize = 10,
      filters: Record<string, unknown> = {},
    ) => ['invoice', 'paginated', companyId, page, pageSize, filters] as const,
    detail: (id?: string) => ['invoice', 'detail', id] as const,
    byId: (id?: string) => ['invoice', id] as const,
    payments: (id?: string) => ['invoice', 'payments', id] as const,
    metrics: (companyId?: string, period = 'current_month', fallback = false) =>
      ['invoice', 'metrics', companyId, period, fallback] as const,
    aging: (companyId?: string, fallback = false) =>
      ['invoice', 'aging', companyId, fallback] as const,
    summary: (companyId?: string, params: Record<string, unknown> = {}) =>
      ['invoice', 'summary', companyId, params] as const,
    recent: (companyId?: string) => ['invoice', 'recent', companyId] as const,
    overdueBulk: (companyId?: string) => ['invoice', 'overdue-bulk', companyId] as const,
    draftBulk: (companyId?: string) => ['invoice', 'draft-bulk', companyId] as const,
    paymentForecast: (companyId?: string, invoiceId?: string) =>
      ['invoice', 'payment-forecast', companyId, invoiceId] as const,
    numberingHealth: (companyId?: string) => ['invoice', 'numbering-health', companyId] as const,
    /** Bandeja de salida: intentos de hacer llegar esta factura a su cliente. */
    deliveries: (companyId?: string, invoiceId?: string) =>
      ['invoice', 'deliveries', companyId, invoiceId] as const,
    /** El plan de cuotas pactado con el cliente. Una factura, un plan. */
    paymentPlan: (companyId?: string, invoiceId?: string) =>
      ['invoice', 'payment-plan', companyId, invoiceId] as const,
    /** La marca de la empresa en sus facturas: layout, colores y pie legal. */
    templates: (companyId?: string) => ['invoice', 'templates', companyId] as const,
    tax: (companyId?: string, year?: number | string) =>
      ['invoice', 'tax', companyId, year] as const,
  },
} as const;
